import depsloader from "../../../depsloader";

const SEARCH_URL = "https://api.screenwork.de/geocoder/search";
const REVERSE_URL = "https://api.screenwork.de/geocoder/reverse";
const REQUEST_TIMEOUT = 10000;

function runtimeConfig() {
  const config = window.swGeocoderConfig;
  if (!config || typeof config.key !== "string" || config.key.trim() === "") {
    return null;
  }

  return {
    key: config.key,
    searchUrl: config.searchUrl || SEARCH_URL,
    reverseUrl: config.reverseUrl || REVERSE_URL
  };
}

function isFiniteNumber(value) {
  return typeof value === "number" && isFinite(value);
}

function validCoordinates(lat, lng) {
  return isFiniteNumber(lat) && isFiniteNumber(lng)
    && lat >= -90 && lat <= 90
    && lng >= -180 && lng <= 180;
}

class Geocoder {
  constructor(axios) {
    this.axios = axios;
    this.axiosRequest = null;
  }

  cancelAutocomplete() {
    if (this.axiosRequest != null) {
      this.axiosRequest.source.cancel();
      this.axiosRequest = null;
    }
  }

  isCancel(error) {
    return !!(error && (error.cancelled || this.axios.isCancel(error)));
  }

  _geoaddressText(g) {
    let s = "";
    if (g.zip && g.city && g.district) {
      s = g.zip + " " + g.city + "–" + g.district;
    } else if (g.zip && g.city) {
      s = g.zip + " " + g.city;
    } else if (g.city && g.district) {
      s = g.city + "–" + g.district;
    } else if (g.city) {
      s = g.city;
    }

    const p = [];

    if (g.label) {
      p.push(g.label);
    }

    if (g.street && g.number) {
      p.push(g.street + " " + g.number);
    } else if (g.street) {
      p.push(g.street);
    }

    if (s) {
      p.push(s);
    }

    if (g.state && !g.street) {
      p.push(g.state);
    }

    if (g.country && !g.street) {
      p.push(g.country);
    }

    return p.join(", ");
  }

  _legacyType(p) {
    if (p.osm_key == "highway") {
      return "street";
    }
    if (p.osm_key == "boundary") {
      return "district";
    }
    if (p.osm_key == "place" && p.osm_value == "suburb") {
      return "district";
    }
    if (p.osm_key == "place" && ["city", "town", "village", "hamlet"].indexOf(p.osm_value) !== -1) {
      return "city";
    }
    if (p.housenumber) {
      return "house";
    }

    return null;
  }

  _geoaddress(l) {
    if (!l || !l.properties || !l.geometry || !Array.isArray(l.geometry.coordinates)) {
      return null;
    }

    const p = l.properties;
    const lng = Number(l.geometry.coordinates[0]);
    const lat = Number(l.geometry.coordinates[1]);
    if (!validCoordinates(lat, lng)) {
      return null;
    }

    const type = p.type || this._legacyType(p);
    const geoaddress = {};

    let street = p.street || null;
    if (type == "street" && p.name) {
      street = p.name;
    }

    if (street) {
      geoaddress["street"] = street;
      if (p.housenumber) {
        geoaddress["number"] = p.housenumber;
      }
    }

    let city = p.city || null;
    let district = p.district || null;

    if (type == "city" && p.name) {
      city = p.name;
      district = null;
    } else if (type == "district" || type == "locality") {
      if (p.name && p.name != city) {
        district = p.name;
      }
    }

    if (p.postcode) {
      geoaddress["zip"] = p.postcode;
    }

    if (city) {
      geoaddress["city"] = city;
    }

    if (district && district != city) {
      geoaddress["district"] = district;
    }

    if (!street && !city && p.name) {
      geoaddress["label"] = p.name;
    }

    geoaddress["lat"] = lat;
    geoaddress["lng"] = lng;

    geoaddress["state"] = p.state;
    geoaddress["country"] = p.country;

    geoaddress["text"] = this._geoaddressText(geoaddress);
    delete geoaddress["label"];

    if (!geoaddress["text"]) {
      return null;
    }

    return geoaddress;
  }

  _features(res) {
    if (res && res.data && Array.isArray(res.data.features)) {
      return res.data.features;
    }

    return [];
  }

  geocode(params, request) {
    const config = runtimeConfig();
    if (!config) {
      return Promise.resolve([]);
    }

    params.append("key", config.key);

    return this.axios.get(config.searchUrl, {
      cancelToken: request.source.token,
      timeout: REQUEST_TIMEOUT,
      params
    }).then(res => {
      const results = [];

      this._features(res).forEach(l => {
        const geoaddress = this._geoaddress(l);
        if (geoaddress) {
          results.push(geoaddress);
        }
      });

      return results;
    }).finally(() => {
      if (this.axiosRequest === request) {
        this.axiosRequest = null;
      }
    });
  }

  autocomplete(q) {
    this.cancelAutocomplete();

    const CancelToken = this.axios.CancelToken;
    const request = {
      source: CancelToken.source()
    };
    this.axiosRequest = request;

    const params = new URLSearchParams();
    params.append("q", q);
    params.append("lang", "de");
    params.append("limit", 10);

    return this.geocode(params, request);
  }

  reverse(lat, lng) {
    const config = runtimeConfig();
    if (!config) {
      return Promise.reject(new Error("geocoder not configured"));
    }

    lat = Number(lat);
    lng = Number(lng);
    if (!validCoordinates(lat, lng)) {
      return Promise.reject(new Error("invalid coordinates"));
    }

    return this.axios.get(config.reverseUrl, {
      timeout: REQUEST_TIMEOUT,
      params: {
        lat: lat,
        lon: lng,
        lang: "de",
        key: config.key
      }
    }).then(res => {
      const features = this._features(res);
      const geoaddress = features.length > 0 ? this._geoaddress(features[0]) : null;
      if (!geoaddress) {
        throw new Error("invalid data");
      }

      return geoaddress;
    });
  }
}

function GeocoderFactory() {
  return new Promise(resolve => {
    depsloader.load("axios").then(axios => {
      resolve(new Geocoder(axios));
    })
  });
}

export { GeocoderFactory };

export default {};
